import 'core-js/stable'
import Vue from 'vue'
import axios from 'axios'
import App from './App'
import router from './router'
import CoreuiVue from '@coreui/vue'
import { iconsSet as icons } from './assets/icons/icons.js'
import store from './store'
import BaseTable from './components/BaseTable.vue'
import DateRangePicker from './components/DateRangePicker.vue'
import { ToastPlugin } from './utils/toast'
import { ButtonSoundPlugin } from './utils/buttonSound'
import { showSessionExpiryOverlay, hideSessionExpiryOverlay } from './utils/sessionExpiryOverlay'
import { applyEnvFavicon } from './utils/envBadge'
import { applyAuthUser, hydrateFromStorage, markIncomplete, resetCompletion } from './utils/profileCompletion'
import posthog from 'posthog-js'

applyEnvFavicon()

// Re-open the profile gate straight away on reload, using the verdict cached
// with `auth_user`, instead of waiting for the /auth/me round trip below.
hydrateFromStorage()

const apiAdress = process.env.VUE_APP_API_URL || 'http://127.0.0.1:8000'

posthog.init(
  process.env.VUE_APP_POSTHOG_KEY || 'phc_ppTwe2eeu8yoUsueuSV87HbtrqgtxKNK92AK5FNvJNjw',
  {
    api_host: process.env.VUE_APP_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    session_recording: {
      maskAllInputs: false,
    },
  },
)
Vue.prototype.$posthog = posthog

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('api_token')

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// ── Global 401 handler ─────────────────────────────────────
//
// When a request comes back 401 the local token is either missing,
// stale, or revoked. Instead of letting every page render an
// "Unauthenticated." alert, we centralise the response: clear auth
// state once, bounce to /login, and reject the in-flight promise so
// downstream code still settles.
//
// The `redirectingToLogin` flag collapses a burst of N concurrent
// 401s into a single navigation — without it, every parallel page
// request would race to push /login and a stale view would flicker
// while the rejected promises drained.
let redirectingToLogin = false

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status
    const requestUrl = (error && error.config && error.config.url) || ''

    // Skip endpoints whose 401 is the *expected* failure mode — the
    // login endpoint returns 401 for wrong credentials, and logout
    // can 401 if the token already expired. Neither should boot the
    // user back to /login.
    const exemptEndpoint = requestUrl.endsWith('/api/login')
      || requestUrl.endsWith('/api/logout')

    const current = (router.currentRoute && router.currentRoute.fullPath) || ''
    const alreadyOnLogin = current === '/login' || current.startsWith('/login?')

    // ── Mandatory profile details ──────────────────────────
    //
    // EnsureProfileComplete refuses every write from a staff member who
    // hasn't filled in their required details. Feed the verdict back into
    // the shared gate state so ProfileCompletionModal opens (or re-opens)
    // over whatever page they were on, then let the caller's own catch
    // handle the rejection as usual.
    if (status === 403 && error.response.data && error.response.data.code === 'PROFILE_INCOMPLETE') {
      markIncomplete(error.response.data.profile_completion)
      return Promise.reject(error)
    }

    if (status === 401 && !exemptEndpoint && !redirectingToLogin) {
      redirectingToLogin = true

      localStorage.removeItem('api_token')
      localStorage.removeItem('roles')
      localStorage.removeItem('auth_user')
      resetCompletion()
      try {
        store.commit('clearOrganisationContext')
        store.commit('clearPermissions')
      } catch (e) {
        // The store may not yet be wired up (very early boot) —
        // localStorage cleanup is the source of truth either way.
      }

      if (alreadyOnLogin) {
        // Already on /login — skip the overlay + navigation entirely.
        setTimeout(() => { redirectingToLogin = false }, 0)
        return Promise.reject(error)
      }

      const next = current && current !== '/login' && !current.startsWith('/login?')
        ? current
        : null

      // ── Fast animated transition ──────────────────────────
      // The overlay's 200ms fade-in runs IN PARALLEL with the route
      // change (Login mounts behind it), so we don't pay a separate
      // perception delay. Total visible lifetime ≈ 700ms.
      //
      // T=0      overlay appended, fade-in starts
      // T=0      router.replace fired (no artificial wait)
      // T=500ms  hide → fade-out starts
      // T=720ms  overlay removed, flag re-armed
      showSessionExpiryOverlay()

      router.replace({
        path: '/login',
        query: next ? { next } : undefined,
      }).catch(() => {
        // NavigationDuplicated / abort errors are not actionable here.
      })

      setTimeout(() => {
        hideSessionExpiryOverlay()
        // Re-arm only after the overlay has finished fading out so
        // a tightly-spaced second 401 doesn't double-trigger.
        setTimeout(() => { redirectingToLogin = false }, 220)
      }, 500)
    }

    return Promise.reject(error)
  },
)

Vue.prototype.$apiAdress = apiAdress
Vue.config.performance = true
Vue.use(CoreuiVue)
Vue.use(ToastPlugin)
Vue.use(ButtonSoundPlugin)
Vue.component('BaseTable', BaseTable)
Vue.component('DateRangePicker', DateRangePicker)

if (localStorage.getItem('api_token')) {
  store.dispatch('fetchOrganisations').catch(() => {
    // Sidebar-level flow will still function with persisted org context.
  })

  axios.get(apiAdress + '/api/auth/me').then((response) => {
    if (response.data && response.data.permissions) {
      store.commit('setPermissions', response.data.permissions)
    }
    if (Array.isArray(response.data && response.data.roles)) {
      localStorage.setItem('roles', response.data.roles.join(','))
    }
    localStorage.setItem('auth_user', JSON.stringify(response.data))
    applyAuthUser(response.data)
    if (response.data && response.data.id) {
      posthog.identify(String(response.data.id), {
        email: response.data.email,
        name: response.data.name,
        roles: response.data.roles,
      })
    }
  }).catch(() => {
    // Token may be stale — let route guards handle the redirect.
  })
}

new Vue({
  el: '#app',
  router,
  store,
  icons,
  template: '<App/>',
  components: {
    App
  },
})
